import { useT } from '@/i18n';
import type { PersonalCouponListItem } from '@restaurant/api-client';
import { Badge, BlockTitle, List, ListItem } from 'framework7-react';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

/**
 * The shared read-only list of private coupons behind both surfaces: the
 * discount detail page (one promotion → who holds each coupon) and the customer
 * record (one customer → which promotion each coupon unlocks). `primary` picks
 * which name each row leads with; the other party is already implied by context.
 * Each row shows the redemption state and the code + issue/use date.
 */
export function CouponHistoryList({
  title,
  emptyText,
  items,
  primary,
  isLoading,
  isError,
}: {
  title: string;
  emptyText: string;
  items: PersonalCouponListItem[];
  primary: 'customer' | 'promotion';
  isLoading?: boolean;
  isError?: boolean;
}) {
  const t = useT();
  const tp = t.promotions;

  return (
    <>
      <BlockTitle>{title}</BlockTitle>
      <List strongIos insetIos dividersIos>
        {isLoading ? (
          <ListItem title={tp.loadingCoupons} />
        ) : isError ? (
          <ListItem title={tp.couldNotLoadCoupons} />
        ) : items.length === 0 ? (
          <ListItem title={emptyText} />
        ) : (
          items.map((c) => {
            const label = (primary === 'customer' ? c.customerName : c.promotionName).trim();
            const when =
              c.isRedeemed && c.redeemedAt
                ? tp.couponRedeemedOn(fmtDate(c.redeemedAt))
                : tp.couponIssuedOn(fmtDate(c.issuedAt));
            return (
              <ListItem
                key={c.id}
                title={label || tp.customerFallback}
                footer={`${c.code} · ${when}`}
              >
                <Badge slot="after" color={c.isRedeemed ? 'gray' : 'green'}>
                  {c.isRedeemed ? tp.couponRedeemed : tp.couponUnused}
                </Badge>
              </ListItem>
            );
          })
        )}
      </List>
    </>
  );
}
